import styled, { css } from 'styled-components';

import type { ThemeColorType, ThemeSpaceType } from 'src/styles/theme';

const baseHeader = css<{ color?: LiteralUnion<ThemeColorType>; $marginBottom?: ThemeSpaceType }>`
  color: ${({ color, theme }) =>
    color ? theme.colors[color as ThemeColorType] ?? color : theme.colors.capsuleBlue50} !important;
  font-family: ${props => props.theme.fonts.circular};
  font-weight: ${props => props.theme.fontWeights.bold};
  padding: ${props => props.theme.space.none} !important;
  margin: ${props => props.theme.space.none} !important;
  margin-bottom: ${props => (props.$marginBottom ? props.theme.space[props.$marginBottom] : 'initial')} !important;
`;

export const DisplayXXXL = styled.h1`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.displayXXXL} !important;
  line-height: ${props => props.theme.lineHeights.displayXXXL} !important;
`;

export const DisplayXXL = styled.h2`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.displayXXL} !important;
  line-height: ${props => props.theme.lineHeights.displayXXL} !important;
`;

export const DisplayXL = styled.h3`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.displayXL} !important;
  line-height: ${props => props.theme.lineHeights.displayXL} !important;
  margin-bottom: ${props => (props.$marginBottom ? props.theme.space[props.$marginBottom] : 'initial')} !important;
`;

export const H1 = styled.h1`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.headerOne} !important;
  line-height: ${props => props.theme.lineHeights.headerOne} !important;
  color: ${({ color, theme }) =>
    color ? theme.colors[color as ThemeColorType] ?? color : theme.colors.capsuleBlue50} !important;
`;

export const H2 = styled.h2`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.headerTwo} !important;
  line-height: ${props => props.theme.lineHeights.headerTwo} !important;
`;

export const H3 = styled.h3`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.headerThree} !important;
  line-height: ${props => props.theme.lineHeights.headerThree} !important;
`;

export const H4 = styled.h4`
  ${baseHeader}
  font-size: ${props => props.theme.fontSizes.headerFour} !important;
  line-height: ${props => props.theme.lineHeights.headerFour} !important;
`;

export const Quote = styled.h2<{ $marginBottom?: string }>`
  font-family: ${props => props.theme.fonts.mercury} !important;
  font-weight: ${props => props.theme.fontWeights.book} !important;
  font-size: ${props => props.theme.fontSizes.quote} !important;
  text-align: center !important;
`;

const baseBody = css<{ color?: ThemeColorType; $marginBottom?: ThemeSpaceType }>`
  font-family: ${props => props.theme.fonts.circular};
  color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.capsuleBlue50)} !important;
  font-weight: ${props => props.theme.fontWeights.book};
`;

export const Body = styled.p`
  ${baseBody}
  font-family: ${props => props.theme.fonts.circular};
  font-size: ${props => props.theme.fontSizes.body} !important;
  line-height: ${props => props.theme.lineHeights.body} !important;
  ${props => props.$marginBottom && `margin-bottom: ${props.theme.space[props.$marginBottom]} !important;`}
`;

export const BodyBold = styled(props => <Body {...props} />)<{ $marginBottom?: string }>`
  font-weight: ${props => props.theme.fontWeights.bold} !important;
`;

const baseSmall = css`
  ${baseBody}
  font-size: ${props => props.theme.fontSizes.small} !important;
  line-height: ${props => props.theme.lineHeights.small} !important;
  font-weight: ${props => props.theme.fontWeights.semiBold} !important;
  ${props => props.$marginBottom && `margin-bottom: ${props.theme.space[props.$marginBottom]} !important;`}
`;

export const Small = styled.p`
  ${baseSmall}
`;

export const SmallSpacedOut = styled.p`
  ${baseSmall}
  letter-spacing: ${props => props.theme.letterSpacings.one} !important;
`;

const baseCallout = css<{ color?: ThemeColorType; $marginBottom?: ThemeSpaceType }>`
  color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.capsuleBlue50)} !important;
  text-transform: ${props => props.theme.textTransforms.allCaps};
  line-height: ${props => props.theme.lineHeights.callout} !important;
  letter-spacing: ${props => props.theme.letterSpacings.one} !important;
  font-weight: ${props => props.theme.fontWeights.semiBold} !important;
`;

export const Overline = styled.p`
  ${baseCallout}
  font-family: ${props => props.theme.fonts.circular};
  font-size: ${props => props.theme.fontSizes.small} !important;
  color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.capsuleBlue50)} !important;
  margin-bottom: ${props => (props.$marginBottom ? props.theme.space[props.$marginBottom] : 'initial')} !important;
`;

export const Callout = styled.p`
  ${baseCallout}
  font-family: ${props => props.theme.fonts.mercury} !important;
  font-size: ${props => props.theme.fontSizes.callout} !important;
`;

export const CalloutSmall = styled.p`
  ${baseCallout}
  font-family: ${props => props.theme.fonts.mercury} !important;
  font-size: ${props => props.theme.fontSizes.calloutSmall} !important;
`;
